import React, { Fragment, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  FormHelperText,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Avatar,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { information } from '../../CompanyProfile/Icons';
import CustomPanel from '../../common/PanelTemplateWithMandatory';
import { Label, useLabelStyles } from '../../common/Label';
import ChangeEmail from '../Icons/ChangeEmail';
import I18n from '../../../config/locales';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import phoneFieldStyles from '../../common/PhoneField/styles';
import { get, values, isEmpty } from '../../../utils/lodash';
import ApiKeyConfirmationPopup from '../ApiKeyConfirmationPopup';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CopyIcon from '../Icons/Copy.svg';

const CustomTooltip = withStyles({
  tooltip: {
    fontSize: '12px'
  }
})(Tooltip);

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0, 3, 4, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 1, 4, 1)
    }
  },
  inputWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap'
    }
  },
  emailWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& .MuiIconButton-root': {
      padding: 0
    },
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap'
    }
  },
  inputField: {
    width: '45%',
    minWidth: theme.spacing(30),
    margin: theme.spacing(3, 1, 0, 1),
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginTop: theme.spacing(2)
    }
  },
  innerInput: {
    width: '100%'
  },
  button: {
    color: theme.palette.secondary.main,
    marginTop: theme.spacing(5)
  },
  boxWrapper: {
    border: '1px solid #C1C1C1',
    margin: theme.spacing(2.375, 1, 0, 1),
    backgroundColor: '#FFFDD6',
    padding: theme.spacing(2.625)
  },
  emailChangeText: {
    color: '#4A4A4A',
    opacity: 1,
    fontSize: '16px',
    fontWeight: 700,
    paddingBottom: theme.spacing(2)
  },
  confirmationText: {
    color: '#4A4A4A',
    opacity: 1,
    fontSize: '14px',
    paddingBottom: theme.spacing(3)
  },
  buttonResendText: {
    color: '#1A85E2',
    opacity: 1,
    fontWeight: 700,
    fontSize: '14px',
    cursor: 'pointer',
    paddingRight: theme.spacing(2.125),
    borderRight: '1px solid #1A85E2'
  },
  buttonCancelText: {
    color: '#1A85E2',
    opacity: 1,
    fontWeight: 700,
    fontSize: '14px',
    paddingLeft: theme.spacing(2.125),
    cursor: 'pointer'
  },
  changeEmail: {
    cursor: 'pointer'
  },
  displayNone: {
    display: 'none'
  },
  buttonClass: {
    height: '25px !important',
    width: '32px !important',
    border: '0px !important',
    background: 'white !important',
    '& .selected-flag': {
      width: '32px !important'
    },
    '& div': {
      padding: '0px !important'
    },
    '&:hover': {
      border: '0px !important'
    },
    '&:focus': {
      border: '0px !important'
    }
  },
  ...phoneFieldStyles(theme),
  mainApiKeyWrapper: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid rgba(0, 0, 0, 0.42) !important',
    paddingBottom: theme.spacing(0.75),
    marginTop: theme.spacing(0.25)
  },
  apiKeyPlaceholder: {
    width: '80%',
    marginBottom: theme.spacing(-0.5)
  },
  styleCopyIcon: {
    height: theme.spacing(2.125),
    width: theme.spacing(2.125),
    cursor: 'pointer'
  },
  loaderApiKey: {
    marginLeft: theme.spacing(4)
  },
  styleApiKeyLabel: {
    marginBottom: theme.spacing(-0.5),
    fontSize: theme.spacing(1.625)
  },
  styleVisibilityIcon: {
    height: theme.spacing(2.5),
    width: theme.spacing(2.5),
    marginLeft: theme.spacing(0.5),
    marginBottom: theme.spacing(-0.75),
    cursor: 'pointer'
  },
  generateApiKeyButton: {
    border: '1px solid #F3731F',
    color: '#F3731F',
    width: '16%',
    marginLeft: 'auto',
    textTransform: 'none',
    height: theme.spacing(3)
  },
  styleApiKeySubtext: {
    color: '#919191',
    fontSize: 13,
    marginTop: theme.spacing(1)
  },
  iconButton: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    padding: theme.spacing(0),
    marginLeft: theme.spacing(1)
  },
  infoIcon: {
    padding: theme.spacing(0)
  },
  profileLabel:{
    textTransform: 'none !important'
  },
  profileInputLabel:{
    zIndex: 1
  }

}));

const AccountDetail = props => {
  const classes = useStyles();
  const labelClass = useLabelStyles();
  const {
    accountDetails,
    handleChange,
    errorData,
    handleSave,
    newEmail,
    resendConfirmationEmail,
    cancelEmailChange,
    getAccountData,
    generateApiKey,
    apiKey,
    currentlySendingApiGenerateRequest,
    accountDetail,
    belongsToUs,
    jobProfileList,
    show_job_profile
  } = props;
const profile_list = jobProfileList.map(([key, value]) => ({ key, value }));
  const handleOnPhoneNumberChange = (value, data, event, formattedValue) => {
    if (!get(event, 'target.value', '')) {
      value = data.dialCode;
      event = { target: { value: `+${value}` } };
    }
    handleChange(event, 'phone');
  };

  const handleGenerateAPIKey = () => {
    generateApiKey();
    setShowApiConfirmationPopup(false);
    setVisibilityOff(false);
  };

  const handleCopyApiKey = () => {
    const containerId = 'api-key-account-details';
    let range = document.createRange();
    range.selectNode(document.getElementById(containerId));
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);
    document.execCommand('copy');
  };

  const { firstName, lastName, email, phone, companyName, profile_id} = accountDetails;
  const { is_installer_account, self_installer, is_distributor, company_id } = accountDetail
  const [isChangeEmail, setIsChangeEmail] = useState(false);
  const [visibilityOff, setVisibilityOff] = useState(true);
  const [showApiConfirmationPopup, setShowApiConfirmationPopup] = useState(false);
  const isPhoneFieldError = get(errorData, 'phone.error');
  const phoneFieldErrorMessage = get(errorData, 'phone.errorMessage');
  const isApiKeyAvailable = !isEmpty(apiKey);
  const roles = get(accountDetail, 'roles', []) || [];
  const isAdmin = roles.includes('admin');
  const isDemo = roles.includes('demo')
  const can_view_job_profile = ((show_job_profile && ((is_installer_account || company_id !== null ) || (!self_installer && !is_distributor && isDemo))) || false )
  return (
    <Fragment>
      <CustomPanel header={I18n.t('accountInfo.account_details')}>
        <Box className={classes.root}>
          <Grid className={classes.emailWrapper}>
            <Grid className={classes.inputField}>
              <TextField
                id={I18n.t('accountDetails.email')}
                className={clsx(classes.innerInput, labelClass.customLabel)}
                label={<Label text={I18n.t('accountDetails.email')} isRequired />}
                value={email}
                onChange={e => handleChange(e, 'email')}
                disabled={!isChangeEmail}
              />
            </Grid>
            {newEmail ? (
              <Box className={classes.boxWrapper}>
                <Typography className={classes.emailChangeText}>{I18n.t('accountDetails.emailChangeText')}</Typography>
                <Typography className={classes.confirmationText}>
                  {I18n.t('accountDetails.confirmationText', { email: newEmail })}
                </Typography>
                <Box display="flex" alignItems="center">
                  <Typography
                    className={classes.buttonResendText}
                    onClick={() => {
                      resendConfirmationEmail({
                        successCbk: () => {
                          getAccountData({});
                        }
                      });
                    }}
                  >
                    {I18n.t('accountDetails.resendConfirmationEmail')}{' '}
                  </Typography>
                  <Typography
                    className={classes.buttonCancelText}
                    onClick={() => {
                      cancelEmailChange({
                        successCbk: () => {
                          window.location.reload();
                        }
                      });
                    }}
                  >
                    {I18n.t('accountDetails.cancelChange')}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Fragment>
                <Typography
                  className={clsx(classes.changeEmail, isChangeEmail && classes.displayNone)}
                  onClick={() => setIsChangeEmail(true)}
                >
                  <ChangeEmail />
                </Typography>
              </Fragment>
            )}
          </Grid>
          <Grid className={classes.inputWrapper}>
            <TextField
              id="first_name"
              className={clsx(classes.inputField, labelClass.customLabel)}
              value={firstName}
              label={<Label text={I18n.t('accountDetails.firstName')} isRequired />}
              onChange={e => handleChange(e, 'first_name')}
              error={errorData && errorData.first_name && errorData.first_name.error}
              helperText={errorData && errorData.first_name && errorData.first_name.errorMessage}
            />
            <TextField
              id="last_name"
              className={clsx(classes.inputField, labelClass.customLabel)}
              value={lastName}
              label={<Label text={I18n.t('accountDetails.lastName')} isRequired />}
              onChange={e => handleChange(e, 'last_name')}
              error={errorData && errorData.last_name && errorData.last_name.error}
              helperText={errorData && errorData.last_name && errorData.last_name.errorMessage}
            />
          </Grid>
          <Grid className={classes.inputWrapper}>
            <FormControl
              className={clsx(classes.inputField, labelClass.customLabel)}
              id="phone-label"
              error={isPhoneFieldError}
            >
              <Label text={I18n.t('accountDetails.phone')} isRequired />
              {values(accountDetails).some(x => x) ? (
                <PhoneInput
                  id="phone"
                  buttonClass={classes.buttonClass}
                  inputClass={clsx(
                    classes.phoneField,
                    classes.phoneInputField,
                    isPhoneFieldError && classes.errorClass
                  )}
                  country="us"
                  value={phone}
                  onChange={handleOnPhoneNumberChange}
                  enableTerritories
                  countryCodeEditable={false}
                  disableCountryGuess
                  excludeCountries={['je']}
                />
              ) : (
                <CircularProgress size={20} />
              )}
              {isPhoneFieldError && <FormHelperText>{phoneFieldErrorMessage}</FormHelperText>}
            </FormControl>
            <TextField
              id="company_id"
              className={clsx(classes.inputField, labelClass.customLabel)}
              value={companyName}
              label={<Label text={I18n.t('accountDetails.company')} isRequired />}
              disabled
            />
          </Grid>
              <Grid className={classes.inputWrapper}>
              {isAdmin && belongsToUs && (
                <Box className={classes.inputField}>
                  {isApiKeyAvailable && (
                    <Typography className={classes.styleApiKeyLabel}>
                      {I18n.t('accountDetails.apiKey')}
                      <img
                        alt="visibilityIcon"
                        src={require(`../Icons/${visibilityOff ? 'EyeOn.svg' : 'EyeOff.svg'}`)}
                        className={classes.styleVisibilityIcon}
                        onClick={() => setVisibilityOff(!visibilityOff)}
                      />
                    </Typography>
                  )}
                  <Box className={classes.mainApiKeyWrapper}>
                    <Box className={classes.apiKeyPlaceholder} id="api-key-account-details">
                      {isApiKeyAvailable ? visibilityOff ? '•'.repeat(20) : apiKey : I18n.t('accountDetails.apiKey')}
                    </Box>
                    {!visibilityOff && (
                      <img
                        alt="copyIcon"
                        src={CopyIcon}
                        className={classes.styleCopyIcon}
                        onClick={() => handleCopyApiKey()}
                      />
                    )}
                    {currentlySendingApiGenerateRequest ? (
                      <CircularProgress className={classes.loaderApiKey} size={20} />
                    ) : (
                      <Button className={classes.generateApiKeyButton} onClick={() => setShowApiConfirmationPopup(true)}>
                        {I18n.t('accountDetails.generate')}
                      </Button>
                    )}
                  </Box>
                  <Box className={classes.styleApiKeySubtext}>({I18n.t('accountDetails.apiKeySubtext')})</Box>
                </Box>
                )}
              
              {can_view_job_profile && <FormControl
            id="profile_id"
            className={clsx(classes.inputField, labelClass.customLabel)}
            error={errorData && errorData.profile_id && errorData.profile_id.error}
          > 
             <InputLabel id="profile-select-label" className={classes.profileInputLabel}>
              <Label text={I18n.t('accountDetails.job_profile.label')} className={classes.profileLabel} isRequired>
              <CustomTooltip title={I18n.t('accountDetails.job_profile.tooltip_msg')}>
              <IconButton aria-label="info" className={classes.infoIcon}>
              <Avatar variant="square" src={information} className={`${classes.iconButton} ${classes.tooltipIcon}`} />
              </IconButton>
             </CustomTooltip>
             </Label>

            </InputLabel>
            <Select
              IconComponent={props => <ExpandMoreIcon className="MuiSelect-icon" />}
              value={profile_id}
              labelId="profile-select-label"
              onChange={e => handleChange(e, 'profile_id')}
              id="profile-select"
            >
              {profile_list &&
                profile_list.map(profile=> {
                  return (
                    <MenuItem key={profile.key} value={profile.key}>
                      {profile.value}
                    </MenuItem>
                  );
                })}
            </Select>
            {errorData &&
            errorData.profile_id &&
            errorData.profile_id.error && <FormHelperText>{errorData.profile_id.errorMessage}</FormHelperText>}
          </FormControl>}
              </Grid>
          <Button
            className={classes.button}
            size="small"
            variant="contained"
            color="primary"
            onClick={handleSave}
            disableElevation
          >
            {I18n.t('accountDetails.saveDetails')}
          </Button>
        </Box>
      </CustomPanel>
      <ApiKeyConfirmationPopup
        showApiConfirmationPopup={showApiConfirmationPopup}
        handleContinue={handleGenerateAPIKey}
        handleClose={() => setShowApiConfirmationPopup(false)}
        isApiKeyAvailable
      />
    </Fragment>
  );
};

export default AccountDetail;
